import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { filter, firstValueFrom, Subscription, combineLatest, interval } from 'rxjs';
import { ExperimentStage } from '../../models/experimentStageEnum';

@Component({
  selector: 'app-to-be-uploaded-experiment-list',
  imports: [CommonModule],
  templateUrl: './to-be-uploaded-experiment-list.component.html',
  styleUrl: './to-be-uploaded-experiment-list.component.scss',
})
export class ToBeUploadedExperimentListComponent implements OnInit, OnDestroy {
  private routerSubscription: any;
  private authSubscription?: Subscription;

  allCompletedExperiments: any[] = [];

  @Output() uploadRequested = new EventEmitter<string>();

  isAuthenticated: boolean = false;
  isTokenExpired = false;
  tokenExpiryTime: Date | null = null;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private dbService: NgxIndexedDBService,
    private router: Router
  ) {}

  ngOnInit() {
    this.getCompletedExperiments();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Reload list of ongoing experiment every time this route becomes active
        this.getCompletedExperiments();
      });

    // Check authentication status and token validity
    this.authSubscription = combineLatest([
      this.oidcSecurityService.isAuthenticated(),
      this.oidcSecurityService.getAccessToken(),
      interval(3000) // Check every 3 seconds
    ]).subscribe(([isAuthenticated, accessToken]) => {
      this.isAuthenticated = isAuthenticated && !!accessToken && accessToken.trim() !== '';
      
      if (accessToken) {
        this.checkTokenExpiry(accessToken);
      } else {
        this.isTokenExpired = true;
        this.tokenExpiryTime = null;
      }
    });
  }

  private checkTokenExpiry(token: string) {
    try {
      const decoded = this.decodeJWT(token);
      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        this.isTokenExpired = decoded.exp < currentTime;
        this.tokenExpiryTime = new Date(decoded.exp * 1000);
      } else {
        this.isTokenExpired = true;
        this.tokenExpiryTime = null;
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
      this.isTokenExpired = true;
      this.tokenExpiryTime = null;
    }
  }

  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  async getCompletedExperiments() {
    this.allCompletedExperiments = (
      await firstValueFrom(this.dbService.getAll('participants'))
    ).filter(
      (x: any) => x.experimentProgress.currentStage === ExperimentStage.Complete
    );
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  getLocalUpdateTime(updateTimeUTC: string) {
    return new Date(updateTimeUTC).toLocaleDateString();
  }

  goToUploadData(participantIdentifier: string) {
    this.uploadRequested.emit(participantIdentifier);
  }

  // Method to refresh the list after successful upload
  refreshList() {
    this.getCompletedExperiments();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }
}
